import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { pluck } from 'rxjs/operators';
import { Project } from 'src/app/core/objects/project';
import { ProjectService } from 'src/app/core/services/project.service';
import { UserService } from 'src/app/core/services/user.service';
import { LoadingSpaceType, Trucks } from 'src/app/core/types';
import {
  validateIsStringInArray,
  validateMaxLoadingSpace,
  validateZero,
} from 'src/app/core/validators/validators';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';

@Component({
    selector: 'gl-loading-space-pallet-dimensions',
    templateUrl: './loading-space-pallet-dimensions.component.html',
    styleUrls: ['./loading-space-pallet-dimensions.component.scss'],
    standalone: false
})
export class LoadingSpacePalletDimensionsComponent implements OnInit, OnChanges, OnDestroy {
  @Output() public submitEmitter = new EventEmitter();
  @Output() public formData = new EventEmitter();
  @Output() public deleteLoadingSpace = new EventEmitter();
  @Input() public chosenLoadingSpace: LoadingSpaceType;
  @Input() public loadingSpace: LoadingSpaceType;
  @Input() public typeLoadingSpace: number;
  @Input() public isEdit: boolean;
  @Input() public changeType: boolean;
  @Input() public hideDelete: boolean;
  @Input() public isMyLoadingSpace = false;
  @Input() public dimensionsLocked = false;
  public saveLoadingForm: SaveLoadingForm;
  public form: DimensionsForm = this.generateLoadingSpaceForm();
  public currentLoadingSpace: LoadingSpaceType;
  public loadingSpaces: LoadingSpaceType[];
  private maxLimit = 99;
  public isFormChanged = false;
  private oldFormValue = '';
  private subscription: Subscription;
  private formSubscription: Subscription;
  private formSubscription2: Subscription;
  private loadingSpaceNames: string[];

  constructor(
    private projectService: ProjectService,
    private translate: TranslateService,
    private userService: UserService,
  ) {}

  public ngOnInit() {
    this.isEdit = true;
    this.userService.user.pipe(pluck('trucks')).subscribe((trucks: Trucks) => {
      this.loadingSpaces = trucks.standard.concat(trucks.user);
      this.loadingSpaceNames = this.loadingSpaces.map(
        (loadingSpace) => this.translate.instant(loadingSpace.name) as string,
      );
    });
    this.subscription = this.form.valueChanges.subscribe((values) => {
      if (this.oldFormValue !== this.form.get('name').value) {
        this.isFormChanged = true;
      } else {
        this.isFormChanged = false;
      }
      this.formData.emit(values);
    });
    this.saveLoadingForm = new UntypedFormGroup({
      remember: new UntypedFormControl(false),
    });
    this.form.disable();

    this.formSubscription2 = this.form.controls['palletMaxHeight'].valueChanges.subscribe(
      (palletMaxHeight: number) => {
        this.form.controls['palletHeight'].setValidators([
          Validators.required,
          validateZero,
          Validators.max(palletMaxHeight),
        ]);
        this.form.controls['palletHeight'].updateValueAndValidity({
          onlySelf: false,
          emitEvent: false,
        });
      },
    );

    this.formSubscription = this.form.controls['name'].valueChanges.subscribe((name: string) => {
      this.currentLoadingSpace = this.loadingSpaces.find(
        (loadingSpace) => this.translate.instant(loadingSpace.name) === name,
      );
      const isNameDirty = this.form.controls['name'].dirty;
      const isNameChanged = this.oldFormValue !== this.form.get('name').value;
      const isValidationDisabled = !(isNameChanged && isNameDirty);
      this.form.controls['name'].setValidators([
        Validators.maxLength(255),
        validateIsStringInArray(this.loadingSpaceNames, isValidationDisabled),
      ]);
      this.form.controls['name'].updateValueAndValidity({ onlySelf: false, emitEvent: false });
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (
      changes.chosenLoadingSpace &&
      changes.chosenLoadingSpace.currentValue !== changes.chosenLoadingSpace.previousValue
    ) {
      this.onChosen(changes.chosenLoadingSpace.currentValue as LoadingSpaceType);
    }
    if (changes.dimensionsLocked) {
      this.applyDimensionsLockedState();
    }
    if (changes.isEdit && changes.isEdit.currentValue) {
      setTimeout(() => this.applyDimensionsLockedState(), 0);
    } else if (changes.isEdit && !changes.isEdit.currentValue) {
      setTimeout(() => this.form.disable(), 0);
    }
  }

  private readonly lockedDimensionControls = [
    'length',
    'width',
    'palletHeight',
    'palletWeight',
    'palletMaxCapacity',
    'palletMaxHeight',
    'palletQuantity',
  ] as const;

  private applyDimensionsLockedState(): void {
    if (!this.isEdit) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.form.enable({ emitEvent: false });

    if (this.dimensionsLocked) {
      this.lockedDimensionControls.forEach((controlName) => {
        this.form.get(controlName)?.disable({ emitEvent: false });
      });
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.formSubscription.unsubscribe();
    this.formSubscription2.unsubscribe();
  }

  public isLoadingSpaceEquel() {
    return _.isEqual(this.loadingSpace, this.chosenLoadingSpace);
  }

  public resetForm(): void {
    this.form.reset();
  }

  public reload(): void {
    this.onChosen(this.chosenLoadingSpace);
  }

  public submit(): void {
    if (this.form.valid) {
      const shouldSave = this.saveLoadingForm.value.remember;
      this.submitEmitter.emit(shouldSave);
      this.isFormChanged = false;
    }
  }

  public emitDeleteLoadingSpace(): void {
    this.deleteLoadingSpace.emit();
  }

  public onChosen(loadingSpace: LoadingSpaceType): void {
    if (!loadingSpace) {
      return;
    }
    this.form.reset();
    this.form.updateValueAndValidity();
    const name = loadingSpace.name ? (this.translate.instant(loadingSpace.name) as string) : '';
    this.form.patchValue({
      name: name,
      length: loadingSpace.length,
      width: loadingSpace.width,
      palletHeight: loadingSpace.palletHeight ? loadingSpace.palletHeight : null,
      palletWeight: loadingSpace.palletWeight ? loadingSpace.palletWeight : null,
      palletMaxCapacity: loadingSpace.limit ? loadingSpace.limit : null,
      palletMaxHeight: loadingSpace.palletMaxHeight ? loadingSpace.palletMaxHeight : null,
      palletQuantity: loadingSpace.length ? 1 : null,
    });
    this.oldFormValue = this.form.get('name').value as string;
    this.isFormChanged = false;
    this.applyDimensionsLockedState();
  }

  private generateLoadingSpaceForm(): UntypedFormGroup {
    this.projectService.project.subscribe((project: Project) => {
      if (project.workSpaces) {
        this.maxLimit = project.workSpaces.length < 99 ? 99 - project.workSpaces.length : 0;
      } else {
        this.maxLimit = 99;
      }
    });
    return new UntypedFormGroup({
      name: new UntypedFormControl(null, [Validators.maxLength(255)]),
      length: new UntypedFormControl(null, [Validators.required, validateZero]),
      width: new UntypedFormControl(null, [Validators.required, validateZero]),
      palletHeight: new UntypedFormControl(null, [Validators.required, validateZero]),
      palletWeight: new UntypedFormControl(null, [validateZero]),
      palletMaxCapacity: new UntypedFormControl(null, [validateZero]),
      palletMaxHeight: new UntypedFormControl(null, [Validators.required, validateZero]),
      palletQuantity: new UntypedFormControl(1, [
        Validators.required,
        validateZero,
        validateMaxLoadingSpace(this.maxLimit),
      ]),
    });
  }
}

interface DimensionsForm extends UntypedFormGroup {
  value: {
    name: string;
    length: number;
    width: number;
    palletHeight: number;
    palletWeight: number;
    palletMaxCapacity: number;
    palletMaxHeight: number;
    palletQuantity: number;
  };
}

interface SaveLoadingForm extends UntypedFormGroup {
  value: {
    remember: boolean;
  };
}
